import { Suspense } from 'react';
import { Link } from 'react-router-dom';
import SEOHead from '../../../components/SEOHead';
import Breadcrumb from '../../../components/Breadcrumb';

export default function MadisonPage() {
  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'LocalBusiness',
    name: 'Technelle AI Marketing - Madison',
    description:
      'AI-driven marketing agency serving Madison businesses with advanced automation, lead generation, and growth strategies tailored to Wisconsin\'s capital city.',
    url: 'https://technelle.ai/service-areas/madison',
    telephone: '414-530-9114',
    email: 'melissa@technelle.ai',
    address: {
      '@type': 'PostalAddress',
      streetAddress: '2810 Crossroads Dr Suite 4000',
      addressLocality: 'Waukesha',
      addressRegion: 'WI',
      postalCode: '53189',
      addressCountry: 'US',
    },
    geo: {
      '@type': 'GeoCoordinates',
      latitude: '43.0731',
      longitude: '-89.4012',
    },
    areaServed: {
      '@type': 'City',
      name: 'Madison',
      '@id': 'https://en.wikipedia.org/wiki/Madison,_Wisconsin',
    },
    priceRange: '$$',
    openingHours: 'Mo-Fr 09:00-17:00',
  };

  return (
    <Suspense fallback={<div>Loading...</div>}>
      <SEOHead
        title="AI Marketing Agency Madison WI | Technelle | Wisconsin Capital"
        description="AI marketing and automation for Madison businesses. Technelle helps Madison startups, UW-adjacent companies, and professional firms grow faster with AI-powered marketing. 20+ years Wisconsin experience."
        keywords="AI marketing Madison WI, marketing agency Madison Wisconsin, AI automation Madison, lead generation Madison, Wisconsin capital marketing, Madison business growth, UW Madison business marketing"
        canonical="https://technelle.ai/service-areas/madison"
        ogImage="https://public.readdy.ai/ai/img_res/f8b74a795e8c846a098e041f4c0cd8b1.jpg"
        structuredData={structuredData}
        geoPosition="43.0731;-89.4012"
        geoRegion="US-WI"
        geoPlacename="Madison"
        breadcrumbs={[
          { name: 'Home', url: 'https://technelle.ai/' },
          { name: 'Service Areas', url: 'https://technelle.ai/service-areas' },
          { name: 'Madison', url: 'https://technelle.ai/service-areas/madison' },
        ]}
      />

      <div className="min-h-screen bg-white font-sans">
        {/* Navigation */}
        <nav className="fixed top-0 left-0 right-0 z-50 bg-[#0A1628]">
          <div className="container mx-auto px-4 sm:px-8 h-20 flex items-center justify-between">
            <Link to="/" className="flex items-center gap-3">
              <img
                src="https://storage.readdy-site.link/project_files/2d2ea231-b3ca-4078-9f64-c6062285e3ca/d2c67bee-2653-4219-a221-2e9d779e273e_ChatGPT-Image-Apr-2-2026-02_21_57-PM.png?v=d5a360501a5e3b86884e2dc0f7061a82"
                alt="Technelle - AI Marketing Wisconsin"
                className="h-14"
              />
            </Link>
            <div className="hidden lg:flex items-center gap-8">
              <Link to="/services" className="text-[15px] text-white/80 hover:text-white transition-colors font-medium">Services</Link>
              <Link to="/industries" className="text-[15px] text-white/80 hover:text-white transition-colors font-medium">Industries</Link>
              <Link to="/about" className="text-[15px] text-white/80 hover:text-white transition-colors font-medium">About</Link>
              <Link to="/case-studies" className="text-[15px] text-white/80 hover:text-white transition-colors font-medium">Case Studies</Link>
              <Link to="/service-areas" className="text-[15px] text-[#2196F3] font-medium">Service Areas</Link>
            </div>
            <Link
              to="/contact"
              className="bg-[#2196F3] text-white px-6 py-3 rounded-md text-[15px] font-medium hover:bg-[#1976D2] transition-colors whitespace-nowrap"
            >
              Book Free Strategy Call
            </Link>
          </div>
          <div className="h-[2px] bg-[#2196F3]"></div>
        </nav>

        {/* Breadcrumb */}
        <div className="pt-24 pb-4 bg-white">
          <Breadcrumb />
        </div>

        {/* Hero Section */}
        <section className="relative min-h-[70vh] flex items-center overflow-hidden">
          <div className="absolute inset-0">
            <img
              src="https://readdy.ai/api/search-image?query=Madison%20Wisconsin%20State%20Capitol%20building%20with%20downtown%20skyline%20and%20Lake%20Mendota%20waterfront%20at%20golden%20hour%2C%20professional%20corporate%20photography%20with%20warm%20light%20representing%20innovation%20hub%20and%20thriving%20business%20community%20in%20Wisconsin%20capital%20city%20with%20vibrant%20economic%20growth&width=1920&height=900&seq=madison-hero-003&orientation=landscape"
              alt="Madison Wisconsin State Capitol and Lake Mendota — AI marketing services for Madison businesses"
              className="w-full h-full object-cover object-top"
              loading="eager"
              fetchPriority="high"
              decoding="async"
              width="1920"
              height="900"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/50 to-black/60"></div>
          </div>
          <div className="relative z-10 container mx-auto px-4 sm:px-8 py-20 w-full">
            <div className="max-w-3xl">
              <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm border border-white/20 px-4 py-2 rounded-full mb-6">
                <i className="ri-map-pin-2-line text-[#2196F3]" aria-hidden="true"></i>
                <span className="text-white text-[14px] font-medium">Madison, WI · Wisconsin's Capital City</span>
              </div>
              <h1 className="text-[44px] sm:text-[58px] lg:text-[68px] leading-[1.1] font-bold text-white mb-6 font-serif">
                AI Marketing for<br />
                <span className="text-[#2196F3]">Madison Businesses</span>
              </h1>
              <p className="text-[18px] sm:text-[20px] text-white/90 mb-8 leading-relaxed max-w-2xl">
                Madison is Wisconsin's most competitive business market — home to UW-Madison, Epic Systems, a thriving startup ecosystem, and a professional services sector that punches well above its size. Technelle helps Madison businesses cut through the noise with AI-powered marketing built for how this market actually works.
              </p>
              <div className="flex flex-wrap gap-4">
                <Link
                  to="/contact"
                  className="bg-[#2196F3] text-white px-8 py-4 rounded-md text-[16px] font-semibold hover:bg-[#1976D2] transition-colors whitespace-nowrap"
                >
                  Book Your Free Strategy Session
                </Link>
                <a
                  href="tel:414-530-9114"
                  className="bg-white/10 backdrop-blur-sm border border-white/30 text-white px-8 py-4 rounded-md text-[16px] font-medium hover:bg-white/20 transition-colors whitespace-nowrap"
                >
                  Call (414) 530-9114
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* Stats Bar */}
        <section className="bg-[#1a1a1a] py-10">
          <div className="container mx-auto px-8">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-8 text-center">
              {[
                { value: '20+', label: 'Years in Wisconsin' },
                { value: '1,000+', label: 'Projects Delivered' },
                { value: '3x', label: 'Average Client ROI' },
                { value: '8+', label: 'Industries Served' },
              ].map((stat, i) => (
                <div key={i}>
                  <div className="text-[36px] font-bold text-[#2196F3] mb-1">{stat.value}</div>
                  <div className="text-[14px] text-white/70">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Madison Market Context */}
        <section className="py-20 bg-white">
          <div className="container mx-auto px-4 sm:px-8">
            <div className="max-w-6xl mx-auto grid lg:grid-cols-2 gap-16 items-center">
              <div>
                <div className="text-[13px] uppercase tracking-widest text-[#2196F3] font-semibold mb-4">The Madison Market</div>
                <h2 className="text-[38px] font-serif font-bold text-[#1a1a1a] mb-6 leading-tight">
                  Madison Buyers Are Sophisticated.<br />Your Marketing Needs to Match.
                </h2>
                <div className="space-y-5 text-[16px] text-gray-700 leading-relaxed">
                  <p>
                    Madison is unlike any other Wisconsin market. The presence of UW-Madison — one of the top research universities in the country — creates a buyer base that is educated, skeptical of hype, and quick to compare options. Epic Systems, American Family Insurance, and a dense cluster of healthcare and biotech companies mean that B2B buyers here are sophisticated and have high expectations.
                  </p>
                  <p>
                    The Isthmus, the Capitol Square, and the State Street corridor create a downtown ecosystem where local businesses compete with national chains for a highly engaged, values-driven consumer base. Meanwhile, the suburbs — Middleton, Fitchburg, Sun Prairie, Verona — are growing fast and represent significant untapped opportunity for businesses that know how to reach them.
                  </p>
                  <p>
                    Technelle brings 20+ years of Wisconsin market experience to Madison businesses. We understand the nuances of competing in a market where your buyers have done their research — and we build marketing systems that meet them where they are.
                  </p>
                </div>
              </div>
              <div className="relative rounded-2xl overflow-hidden">
                <img
                  src="https://readdy.ai/api/search-image?query=Madison%20Wisconsin%20State%20Street%20pedestrian%20mall%20with%20Capitol%20building%20in%20background%20and%20vibrant%20local%20businesses%20and%20restaurants%2C%20professional%20corporate%20photography%20representing%20thriving%20downtown%20business%20community%20and%20innovation%20culture%20in%20Wisconsin%20capital%20city&width=700&height=560&seq=madison-market-001&orientation=landscape"
                  alt="Madison Wisconsin State Street and Capitol Square — representing the city's vibrant business ecosystem"
                  className="w-full h-[420px] object-cover object-top"
                  loading="lazy"
                  decoding="async"
                  width="700"
                  height="420"
                />
                <div className="absolute bottom-6 left-6 right-6">
                  <div className="bg-white/95 backdrop-blur-sm rounded-xl px-6 py-4">
                    <div className="text-[13px] text-gray-500 mb-1">Wisconsin's Innovation Capital</div>
                    <div className="text-[18px] font-bold text-[#1a1a1a]">Madison, Wisconsin</div>
                    <div className="text-[13px] text-[#2196F3] font-medium mt-1">Population 270,000+ · Dane County</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Industries We Serve in Madison */}
        <section className="py-20 bg-gray-50">
          <div className="container mx-auto px-4 sm:px-8">
            <div className="max-w-6xl mx-auto">
              <div className="text-center mb-14">
                <div className="text-[13px] uppercase tracking-widest text-[#2196F3] font-semibold mb-3">Madison Industries</div>
                <h2 className="text-[40px] font-serif font-bold text-[#1a1a1a] mb-4">
                  Built for Madison's Economy
                </h2>
                <p className="text-[17px] text-gray-600 max-w-2xl mx-auto leading-relaxed">
                  We specialize in the industries that drive Dane County — not generic playbooks, but strategies built for how Madison businesses actually compete.
                </p>
              </div>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {[
                  {
                    icon: 'ri-code-s-slash-line',
                    title: 'Technology & SaaS',
                    body: 'Madison\'s tech scene is anchored by Epic Systems and a growing cluster of SaaS startups. We help tech companies generate qualified pipeline, shorten sales cycles, and build the kind of brand authority that attracts enterprise buyers.',
                  },
                  {
                    icon: 'ri-hospital-line',
                    title: 'Healthcare & Biotech',
                    body: 'UW Health, SSM Health, and a dense biotech corridor make healthcare one of Madison\'s largest sectors. We help healthcare businesses and practices grow patient volume with compliant, targeted AI marketing.',
                  },
                  {
                    icon: 'ri-briefcase-4-line',
                    title: 'Professional Services',
                    body: 'Law firms, accounting practices, and consulting firms in Madison compete in a market where buyers do their research. We build the digital authority and lead generation systems that put you at the top of that research.',
                  },
                  {
                    icon: 'ri-graduation-cap-line',
                    title: 'Education & Training',
                    body: 'From UW-adjacent businesses to private training providers, Madison\'s education sector benefits from our enrollment marketing, content strategy, and AI-powered lead nurturing systems.',
                  },
                  {
                    icon: 'ri-restaurant-line',
                    title: 'Hospitality & Food Service',
                    body: 'Madison\'s restaurant and hospitality scene is one of the most competitive in the Midwest. We help Madison restaurants, hotels, and event venues fill seats and build loyal local followings.',
                  },
                  {
                    icon: 'ri-home-gear-line',
                    title: 'Real Estate & Construction',
                    body: 'Dane County\'s real estate market is one of Wisconsin\'s hottest. We help agents, brokers, and construction companies generate qualified buyer and seller leads with AI-powered marketing.',
                  },
                ].map((item, i) => (
                  <div key={i} className="bg-white rounded-xl p-7 border border-gray-100 hover:border-[#2196F3]/30 transition-all">
                    <div className="w-12 h-12 bg-[#2196F3]/10 rounded-lg flex items-center justify-center mb-5">
                      <i className={`${item.icon} text-[#2196F3] text-xl`} aria-hidden="true"></i>
                    </div>
                    <h3 className="text-[18px] font-bold text-[#1a1a1a] mb-3">{item.title}</h3>
                    <p className="text-[14px] text-gray-600 leading-relaxed">{item.body}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Services for Madison */}
        <section className="py-20 bg-white">
          <div className="container mx-auto px-4 sm:px-8">
            <div className="max-w-6xl mx-auto">
              <div className="text-center mb-14">
                <div className="text-[13px] uppercase tracking-widest text-[#2196F3] font-semibold mb-3">What We Do</div>
                <h2 className="text-[40px] font-serif font-bold text-[#1a1a1a] mb-4">
                  AI Marketing Services for Madison
                </h2>
                <p className="text-[17px] text-gray-600 max-w-2xl mx-auto leading-relaxed">
                  The same senior-level AI marketing expertise that drives results in Milwaukee and Green Bay — applied to the specific dynamics of Madison's competitive market.
                </p>
              </div>
              <div className="grid md:grid-cols-2 gap-8">
                {[
                  {
                    icon: 'ri-search-eye-line',
                    title: 'SEO & AI Search Visibility',
                    body: 'Madison buyers research before they buy — and they research thoroughly. We build the kind of local SEO authority that puts your business at the top of that research, including the AI-generated answers that now dominate Google results for high-intent searches.',
                    tag: 'Long-term organic growth',
                  },
                  {
                    icon: 'ri-robot-line',
                    title: 'AI Automation & Workflow Optimization',
                    body: 'Madison\'s professional services and tech companies are often the first to recognize the value of AI — but implementation is where most fall short. We identify where your team is spending time on tasks AI can handle and build the systems to fix it. Most clients recover $30K–$80K in annual value.',
                    tag: 'Cost savings & scale',
                  },
                  {
                    icon: 'ri-article-line',
                    title: 'Content Strategy & Thought Leadership',
                    body: 'In a market full of sophisticated buyers, content is how you build trust before the first conversation. We develop authority-building content strategies that position Madison businesses as the obvious choice in their category — and drive consistent inbound leads.',
                    tag: 'Trust & authority',
                  },
                  {
                    icon: 'ri-mail-send-line',
                    title: 'Email Marketing & Lead Nurturing',
                    body: 'Madison\'s longer B2B sales cycles mean lead nurturing is critical. We build automated email systems that keep your business top of mind through the entire buyer journey — turning cold leads into warm conversations and warm conversations into closed deals.',
                    tag: 'Revenue on autopilot',
                  },
                ].map((svc, i) => (
                  <div key={i} className="bg-gray-50 rounded-2xl p-8 border border-gray-100 hover:border-[#2196F3]/30 transition-all">
                    <div className="w-12 h-12 bg-[#1a1a1a] rounded-lg flex items-center justify-center mb-5">
                      <i className={`${svc.icon} text-white text-xl`} aria-hidden="true"></i>
                    </div>
                    <h3 className="text-[22px] font-bold text-[#1a1a1a] mb-3">{svc.title}</h3>
                    <p className="text-[15px] text-gray-600 leading-relaxed mb-4">{svc.body}</p>
                    <span className="inline-block bg-[#2196F3]/10 text-[#2196F3] text-[12px] font-semibold uppercase tracking-wider px-3 py-1.5 rounded-full">
                      {svc.tag}
                    </span>
                  </div>
                ))}
              </div>
              <div className="text-center mt-10">
                <Link
                  to="/services"
                  className="inline-flex items-center gap-2 text-[#2196F3] text-[15px] font-semibold hover:underline"
                >
                  See all services <i className="ri-arrow-right-line" aria-hidden="true"></i>
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* Why Madison Businesses Choose Technelle */}
        <section className="py-20 bg-[#0A1628]">
          <div className="container mx-auto px-4 sm:px-8">
            <div className="max-w-5xl mx-auto">
              <div className="text-center mb-14">
                <div className="text-[13px] uppercase tracking-widest text-[#2196F3] font-semibold mb-3">Why Technelle</div>
                <h2 className="text-[40px] font-serif font-bold text-white mb-4">
                  A Wisconsin Agency That Understands Madison
                </h2>
                <p className="text-[17px] text-white/75 max-w-2xl mx-auto leading-relaxed">
                  National agencies don't know that Madison buyers are more research-driven than any other Wisconsin market, that the UW ecosystem creates unique B2B dynamics, or that Dane County's suburban growth is creating new opportunities that most businesses are missing. We do.
                </p>
              </div>
              <div className="grid md:grid-cols-3 gap-6">
                {[
                  {
                    icon: 'ri-lightbulb-line',
                    title: 'Innovation-First Approach',
                    body: 'Madison is a market that rewards innovation. We bring the same forward-thinking AI marketing approach that resonates with Madison\'s tech-savvy, research-oriented buyer base — not the same playbook we use everywhere else.',
                  },
                  {
                    icon: 'ri-user-star-line',
                    title: 'Senior-Level Only',
                    body: 'When you work with Technelle, you work directly with Melissa — a 20-year marketing operator. No junior account managers, no outsourced execution, no national playbook applied to your local market.',
                  },
                  {
                    icon: 'ri-bar-chart-grouped-line',
                    title: 'Measurable Results',
                    body: 'We define KPIs before we start and track them throughout. Our Madison clients see an average 3x ROI within the first year — and we can show you exactly how we get there before you commit to anything.',
                  },
                ].map((card, i) => (
                  <div key={i} className="bg-[#0d1f35] border border-white/10 rounded-xl p-7 hover:border-[#2196F3]/50 transition-all">
                    <div className="w-12 h-12 bg-[#2196F3]/15 rounded-lg flex items-center justify-center mb-5">
                      <i className={`${card.icon} text-[#2196F3] text-xl`} aria-hidden="true"></i>
                    </div>
                    <h3 className="text-[18px] font-bold text-white mb-3">{card.title}</h3>
                    <p className="text-[14px] text-white/70 leading-relaxed">{card.body}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Testimonial */}
        <section className="bg-white py-16 border-t border-gray-100">
          <div className="container mx-auto px-4 sm:px-8">
            <div className="max-w-4xl mx-auto text-center">
              <i className="ri-double-quotes-l text-gray-200 text-[80px] leading-none" aria-hidden="true"></i>
              <blockquote className="text-[26px] sm:text-[30px] font-serif text-[#1a1a1a] leading-tight mb-6 -mt-6">
                "If you're looking for a company that delivers top-notch work and values collaboration, you can stop searching. You've found it with Technelle!"
              </blockquote>
              <footer>
                <strong className="text-[#1a1a1a] text-[17px]">Tony Wojak</strong>
                <span className="text-gray-500 block text-[14px] mt-1">Owner, Tony's Heating &amp; Cooling — Milwaukee, WI</span>
              </footer>
            </div>
          </div>
        </section>

        {/* Other Service Areas */}
        <section className="py-16 bg-gray-50">
          <div className="container mx-auto px-4 sm:px-8">
            <div className="max-w-5xl mx-auto">
              <div className="text-center mb-10">
                <div className="text-[13px] uppercase tracking-widest text-[#2196F3] font-semibold mb-3">Also Serving</div>
                <h2 className="text-[28px] font-serif font-bold text-[#1a1a1a]">Other Wisconsin Markets</h2>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
                <Link to="/service-areas/milwaukee" className="bg-white rounded-xl p-6 border border-gray-100 hover:border-[#2196F3]/40 transition-all group">
                  <div className="text-[18px] font-bold text-[#1a1a1a] group-hover:text-[#2196F3] transition-colors mb-2">Milwaukee, WI</div>
                  <p className="text-[14px] text-gray-600 mb-3">Wisconsin's largest market — manufacturing, healthcare, and trade services.</p>
                  <span className="text-[13px] text-[#2196F3] font-semibold flex items-center gap-1">Milwaukee services <i className="ri-arrow-right-line"></i></span>
                </Link>
                <Link to="/service-areas/green-bay" className="bg-white rounded-xl p-6 border border-gray-100 hover:border-[#2196F3]/40 transition-all group">
                  <div className="text-[18px] font-bold text-[#1a1a1a] group-hover:text-[#2196F3] transition-colors mb-2">Green Bay, WI</div>
                  <p className="text-[14px] text-gray-600 mb-3">Northeast Wisconsin's hub — manufacturing, hospitality, and B2B services.</p>
                  <span className="text-[13px] text-[#2196F3] font-semibold flex items-center gap-1">Green Bay services <i className="ri-arrow-right-line"></i></span>
                </Link>
                <Link to="/service-areas" className="bg-white rounded-xl p-6 border border-gray-100 hover:border-[#2196F3]/40 transition-all group">
                  <div className="text-[18px] font-bold text-[#1a1a1a] group-hover:text-[#2196F3] transition-colors mb-2">All of Wisconsin</div>
                  <p className="text-[14px] text-gray-600 mb-3">Waukesha, Appleton, Oshkosh, Eau Claire, and every Wisconsin market statewide.</p>
                  <span className="text-[13px] text-[#2196F3] font-semibold flex items-center gap-1">All service areas <i className="ri-arrow-right-line"></i></span>
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 bg-[#0A1628]">
          <div className="container mx-auto px-4 sm:px-8 text-center">
            <h2 className="text-[40px] sm:text-[52px] font-serif font-bold text-white mb-6 leading-tight">
              Ready to Grow Your<br />
              <span className="text-[#2196F3]">Madison Business?</span>
            </h2>
            <p className="text-[18px] text-white/75 mb-10 max-w-2xl mx-auto leading-relaxed">
              Book a free 30-minute strategy session and walk away with a custom AI marketing roadmap for your Madison business — no pitch, no obligation.
            </p>
            <div className="flex flex-wrap gap-4 justify-center">
              <Link
                to="/contact"
                className="bg-[#2196F3] text-white px-10 py-4 rounded-md text-[16px] font-semibold hover:bg-[#1976D2] transition-colors whitespace-nowrap"
              >
                Book My Free Strategy Session
              </Link>
              <a
                href="tel:414-530-9114"
                className="bg-white/10 backdrop-blur-sm border border-white/30 text-white px-10 py-4 rounded-md text-[16px] font-medium hover:bg-white/20 transition-colors whitespace-nowrap"
              >
                Call (414) 530-9114
              </a>
            </div>
          </div>
        </section>

        {/* Footer */}
        <footer className="bg-[#1a1a1a] text-white py-20">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-12 mb-16">
              <div className="lg:col-span-1">
                <img
                  src="https://storage.readdy-site.link/project_files/2d2ea231-b3ca-4078-9f64-c6062285e3ca/d2c67bee-2653-4219-a221-2e9d779e273e_ChatGPT-Image-Apr-2-2026-02_21_57-PM.png?v=d5a360501a5e3b86884e2dc0f7061a82"
                  alt="Technelle"
                  className="h-12 mb-4"
                  width="48"
                  height="48"
                  loading="lazy"
                  decoding="async"
                />
                <p className="text-[14px] text-gray-400 leading-relaxed">
                  AI-powered marketing for Madison and Dane County businesses. Based in Waukesha, serving the state and beyond.
                </p>
              </div>
              <div>
                <h5 className="text-[14px] uppercase tracking-wider text-gray-400 mb-6 font-semibold">Service Areas</h5>
                <nav className="space-y-3">
                  <Link to="/service-areas/milwaukee" className="block text-white hover:text-[#2196F3] transition-colors text-[15px]">Milwaukee, WI</Link>
                  <Link to="/service-areas/madison" className="block text-[#2196F3] text-[15px] font-medium">Madison, WI</Link>
                  <Link to="/service-areas/green-bay" className="block text-white hover:text-[#2196F3] transition-colors text-[15px]">Green Bay, WI</Link>
                  <Link to="/service-areas" className="block text-white hover:text-[#2196F3] transition-colors text-[15px]">All Service Areas</Link>
                </nav>
              </div>
              <div>
                <h5 className="text-[14px] uppercase tracking-wider text-gray-400 mb-6 font-semibold">Links</h5>
                <nav className="space-y-3">
                  <Link to="/" className="block text-white hover:text-[#2196F3] transition-colors text-[15px]">Home</Link>
                  <Link to="/about" className="block text-white hover:text-[#2196F3] transition-colors text-[15px]">About</Link>
                  <Link to="/services" className="block text-white hover:text-[#2196F3] transition-colors text-[15px]">Services</Link>
                  <Link to="/industries" className="block text-white hover:text-[#2196F3] transition-colors text-[15px]">Industries</Link>
                  <Link to="/case-studies" className="block text-white hover:text-[#2196F3] transition-colors text-[15px]">Case Studies</Link>
                  <Link to="/faq" className="block text-white hover:text-[#2196F3] transition-colors text-[15px]">FAQ</Link>
                  <Link to="/contact" className="block text-white hover:text-[#2196F3] transition-colors text-[15px]">Contact</Link>
                </nav>
              </div>
              <div>
                <h5 className="text-[14px] uppercase tracking-wider text-gray-400 mb-6 font-semibold">Contact</h5>
                <div className="space-y-3">
                  <a href="tel:4145309114" className="block text-white hover:text-[#2196F3] transition-colors text-[15px]">414-530-9114</a>
                  <a href="mailto:melissa@technelle.ai" className="block text-white hover:text-[#2196F3] transition-colors text-[15px]">melissa@technelle.ai</a>
                  <p className="text-[14px] text-gray-400">Waukesha, Wisconsin</p>
                </div>
              </div>
            </div>
            <div className="border-t border-white/10 pt-8">
              <div className="flex flex-col lg:flex-row items-center justify-between gap-6">
                <div className="text-[14px] text-gray-400">© 2026 Technelle. All rights reserved.</div>
                <div className="flex items-center gap-6">
                  <Link to="/privacy-policy" className="text-[14px] text-gray-400 hover:text-white transition-colors">Privacy Policy</Link>
                  <Link to="/terms" className="text-[14px] text-gray-400 hover:text-white transition-colors">Terms</Link>
                </div>
              </div>
            </div>
          </div>
        </footer>
      </div>
    </Suspense>
  );
}
